import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { LocalizedRichDescription } from '../../models/serializable/localized-rich-description.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';
import { RichDescription } from '../../models/serializable/rich-description.js';
import { Language } from '../../models/serializable/language.js';

export class LocalizedRichDescriptionComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'bc238734949a432eba8e7384436c4d1f';
  static readonly SCHEMA_VERSION: number = 3;
  static readonly DEFAULT_RENDER_ORDER: number = 8;

  constructor(
    public readonly descriptions: LocalizedRichDescription,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return LocalizedRichDescriptionComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LocalizedRichDescriptionComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static empty(opts?: { renderOrder?: number }): LocalizedRichDescriptionComponent {
    return new LocalizedRichDescriptionComponent(
        LocalizedRichDescription.empty(),
        opts?.renderOrder ?? LocalizedRichDescriptionComponent.DEFAULT_RENDER_ORDER,
        false,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LocalizedRichDescriptionComponent {
    return new LocalizedRichDescriptionComponent(
        context.fromData<LocalizedRichDescription>(MapUtils.getRaw(map, 'descriptions') as SerializedData),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  static fromRichDescription(description: LocalizedRichDescription): LocalizedRichDescriptionComponent {
    return new LocalizedRichDescriptionComponent(
        description,
        LocalizedRichDescriptionComponent.DEFAULT_RENDER_ORDER,
        true,
    );
  }

  static fromLocalizedRichDescription(
      localizedRichDescripton: LocalizedRichDescription,
  ): LocalizedRichDescriptionComponent {
    return new LocalizedRichDescriptionComponent(
        localizedRichDescripton,
        LocalizedRichDescriptionComponent.DEFAULT_RENDER_ORDER,
        true,
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'descriptions': context.toData(this.descriptions),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new LocalizedRichDescriptionComponent(
        this.descriptions.clone(),
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: LocalizedRichDescriptionComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.descriptions, this.descriptions) &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class LocalizedRichDescriptionComponentBuilder extends DataModelBuilder<LocalizedRichDescriptionComponent> {
  build(context: SerializationContext, map: SerializedData): LocalizedRichDescriptionComponent {
    return LocalizedRichDescriptionComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LocalizedRichDescriptionComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(LocalizedRichDescriptionComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', LocalizedRichDescriptionComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    // In this version `description` of type [RichDescription] was replaced by
    // `descriptions` of type [LocalizedRichDescription].
    if(version < 3) {
      const description = MapUtils.get<SerializedData>(map, 'description');

      if(description) {
        const descriptions = context.fromData<RichDescription>(description);
        const language = context.storage.get<Language>('language', Language.English) ?? Language.English;
        MapUtils.set(
            map,
            'descriptions',
            context.toData(LocalizedRichDescription.forLanguage(language, descriptions))
        );
      }

      version = 3;
    }
    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
